import { motion } from "framer-motion";
import { Dumbbell, Monitor, Music, Plane, ChefHat, TreePine, Star, CheckCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { REVIEWS, FAQ_ITEMS, USE_CASES } from "@/lib/product-data";
import { StarRating } from "./HeroSection";

const glass = "bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-2xl";
const glassHover = "hover:bg-white/[0.06] hover:border-white/15 hover:-translate-y-1 transition-all duration-300";
const anim = { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

const UC_ICONS = [Dumbbell, Monitor, Music, Plane, ChefHat, TreePine];

export function UseCases() {
  return (
    <motion.section data-testid="use-cases" className="py-24 md:py-32 relative" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={anim}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-14">
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-cyan-400 block mb-3">Designed For</span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">Every Moment That Needs a Breeze</h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-5">
          {USE_CASES.map((uc, i) => {
            const Icon = UC_ICONS[i];
            return (
              <div key={i} data-testid={`use-case-card-${i}`} className={`${glass} ${glassHover} p-6 sm:p-8 text-center cursor-default group`}>
                <div className="w-12 h-12 rounded-xl bg-cyan-400/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-cyan-400/20 transition-colors">
                  <Icon size={22} className="text-cyan-400" />
                </div>
                <div className="font-semibold text-sm sm:text-base mb-1">{uc.title}</div>
                <div className="text-xs sm:text-sm text-slate-400">{uc.desc}</div>
              </div>
            );
          })}
        </div>
      </div>
    </motion.section>
  );
}

export function ReviewsSection() {
  return (
    <motion.section data-testid="reviews-section" className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={anim}>
      <div className="text-center mb-14">
        <span className="text-xs font-bold tracking-[0.2em] uppercase text-cyan-400 block mb-3">Customer Love</span>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">What People Are Saying</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
        {REVIEWS.map((r, i) => (
          <div key={i} data-testid={`review-card-${i}`} className={`${glass} ${glassHover} p-6`}>
            <StarRating count={r.rating} />
            <p className="text-sm leading-relaxed text-slate-300 mt-3 mb-4">"{r.text}"</p>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">{r.name}</span>
                {r.verified && (
                  <span className="inline-flex items-center gap-1 text-cyan-400 text-xs font-semibold">
                    <CheckCircle size={12} /> Verified
                  </span>
                )}
              </div>
              <span className="text-xs text-slate-500">{r.date}</span>
            </div>
          </div>
        ))}
      </div>
    </motion.section>
  );
}

export function FaqSection() {
  return (
    <motion.section data-testid="faq-section" className="py-24 md:py-32 relative" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={anim}>
      <div className="max-w-2xl mx-auto px-6 md:px-12">
        <div className="text-center mb-14">
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-cyan-400 block mb-3">Questions?</span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">We've Got Answers</h2>
        </div>
        <Accordion type="single" collapsible className="space-y-3">
          {FAQ_ITEMS.map((item, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              data-testid={`faq-item-${i}`}
              className="bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden px-6 data-[state=open]:bg-white/[0.06] data-[state=open]:border-cyan-400/20 transition-colors"
            >
              <AccordionTrigger className="text-sm sm:text-base font-semibold hover:no-underline py-5 text-left text-white [&>svg]:text-cyan-400">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-slate-400 text-sm leading-relaxed pb-5">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </motion.section>
  );
}
